/**
 * DataGrid Column Configuration Utilities
 * Provides reusable column configuration presets and builders
 * Eliminates duplicate column configuration patterns
 */

/**
 * Default column configuration presets
 */
export const COLUMN_PRESETS = {
  // Standard center-aligned column
  CENTER: {
    headerAlign: 'center',
    align: 'center',
  },

  // Standard column with flex and minWidth
  STANDARD: {
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 150,
  },

  // Compact column
  COMPACT: {
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 100,
  },

  // Wide column (for descriptions, addresses, etc.)
  WIDE: {
    headerAlign: 'center',
    align: 'center',
    flex: 2,
    minWidth: 200,
  },

  // Extra wide column
  EXTRA_WIDE: {
    headerAlign: 'center',
    align: 'center',
    flex: 3,
    minWidth: 250,
  },

  // ID column (smaller)
  ID: {
    headerAlign: 'center',
    align: 'center',
    flex: 0.5,
    minWidth: 80,
  },

  // Action column (non-sortable, non-filterable)
  ACTION: {
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    filterable: false,
    flex: 1,
  },
};

/**
 * Creates a basic column configuration
 * @param {string} field - Column field name
 * @param {string} headerName - Column header display name
 * @param {Object} options - Additional column options
 * @returns {Object} Column configuration
 */
export const createColumn = (field, headerName, options = {}) => {
  return {
    field,
    headerName,
    ...COLUMN_PRESETS.STANDARD,
    ...options,
  };
};

/**
 * Creates a center-aligned column
 * @param {string} field - Column field name
 * @param {string} headerName - Column header display name
 * @param {Object} options - Additional column options
 * @returns {Object} Column configuration
 */
export const createCenterColumn = (field, headerName, options = {}) => {
  return {
    field,
    headerName,
    ...COLUMN_PRESETS.CENTER,
    ...options,
  };
};

/**
 * Creates a compact column
 * @param {string} field - Column field name
 * @param {string} headerName - Column header display name
 * @param {Object} options - Additional column options
 * @returns {Object} Column configuration
 */
export const createCompactColumn = (field, headerName, options = {}) => {
  return {
    field,
    headerName,
    ...COLUMN_PRESETS.COMPACT,
    ...options,
  };
};

/**
 * Creates a wide column (good for descriptions, addresses)
 * @param {string} field - Column field name
 * @param {string} headerName - Column header display name
 * @param {Object} options - Additional column options
 * @returns {Object} Column configuration
 */
export const createWideColumn = (field, headerName, options = {}) => {
  return {
    field,
    headerName,
    ...COLUMN_PRESETS.WIDE,
    ...options,
  };
};

/**
 * Creates an ID column
 * @param {string} field - Column field name (default: 'id')
 * @param {string} headerName - Column header display name
 * @param {Object} options - Additional column options
 * @returns {Object} Column configuration
 */
export const createIdColumn = (field = 'id', headerName = 'ID', options = {}) => {
  return {
    field,
    headerName,
    ...COLUMN_PRESETS.ID,
    ...options,
  };
};

/**
 * Creates an action column (typically for buttons/icons)
 * @param {Function} renderCell - Custom render function for the cell
 * @param {Object} options - Additional column options
 * @returns {Object} Column configuration
 */
export const createActionColumn = (renderCell, options = {}) => {
  return {
    field: 'actions',
    headerName: '',
    ...COLUMN_PRESETS.ACTION,
    renderCell,
    ...options,
  };
};

/**
 * Creates a status column with custom renderer
 * @param {string} field - Column field name
 * @param {string} headerName - Column header display name
 * @param {Function} renderCell - Custom render function for the status
 * @param {Object} options - Additional column options
 * @returns {Object} Column configuration
 */
export const createStatusColumn = (field, headerName, renderCell, options = {}) => {
  return {
    field,
    headerName,
    ...COLUMN_PRESETS.COMPACT,
    renderCell,
    ...options,
  };
};

/**
 * Creates multiple columns from a field list
 * @param {Array} fields - Array of objects with {field, headerName, ...options}
 * @param {string} preset - Preset to apply (default: 'STANDARD')
 * @returns {Array} Array of column configurations
 */
export const createColumns = (fields, preset = 'STANDARD') => {
  const presetConfig = COLUMN_PRESETS[preset] || COLUMN_PRESETS.STANDARD;

  return fields.map(({ field, headerName, ...options }) => ({
    field,
    headerName,
    ...presetConfig,
    ...options,
  }));
};

/**
 * Creates dynamic columns from object data
 * @param {Array} fieldIds - Array of field IDs
 * @param {Array} objectData - Object data from localStorage
 * @param {Object} options - Column options
 * @returns {Array} Array of column configurations
 */
export const createDynamicColumns = (fieldIds, objectData, options = {}) => {
  return fieldIds.map((fieldId) => {
    const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));
    const columnName = objectInfo?.name || `Field ${fieldId}`;

    return {
      field: `field_${fieldId}`,
      headerName: columnName,
      ...COLUMN_PRESETS.STANDARD,
      ...options,
    };
  });
};

/**
 * Merges column configurations with common settings
 * @param {Array} columns - Array of column configurations
 * @param {Object} commonSettings - Settings to apply to all columns
 * @returns {Array} Array of merged column configurations
 */
export const mergeColumnSettings = (columns, commonSettings) => {
  return columns.map((column) => ({
    ...commonSettings,
    ...column,
  }));
};

/**
 * Common column configurations for typical use cases
 */
export const COMMON_COLUMNS = {
  /**
   * Creates a standard name column
   */
  name: (headerName = 'Name', options = {}) =>
    createColumn('name', headerName, options),

  /**
   * Creates a standard code column
   */
  code: (headerName = 'Code', options = {}) =>
    createColumn('code', headerName, options),

  /**
   * Creates a standard address column
   */
  address: (headerName = 'Address', options = {}) =>
    createWideColumn('address', headerName, options),

  /**
   * Creates a standard region column
   */
  region: (headerName = 'Region', options = {}) =>
    createColumn('region', headerName, options),

  /**
   * Creates a standard date column
   */
  date: (headerName = 'Date', options = {}) =>
    createColumn('date', headerName, options),
};
